import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

const LOGO_URL = "/images/logo/logo.webp";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  const scrollToSection = useCallback(
    (selector: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      setOpen(false);
      if (location.pathname === "/") {
        const el = document.querySelector(selector);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        navigate("/");
        setTimeout(() => {
          const el = document.querySelector(selector);
          if (el) {
            el.scrollIntoView({ behavior: "smooth" });
          }
        }, 250);
      }
    },
    [location.pathname, navigate],
  );

  const isHome = location.pathname === "/";
  const showSolid = scrolled || !isHome;

  const linkBase = "text-sm font-medium tracking-wide transition-colors whitespace-nowrap cursor-pointer";
  const linkStyle = showSolid
    ? "text-foreground-800 hover:text-accent-600"
    : "text-background-50 hover:text-accent-200";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showSolid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src={LOGO_URL}
            alt="Mateo Teneryfa"
            className="h-9 w-auto rounded-md object-contain"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className={`font-heading text-lg md:text-xl font-semibold ${showSolid ? "text-foreground-950" : "text-background-50"}`}>
              Mateo Teneryfa
            </span>
            <span className={`text-[10px] md:text-xs uppercase tracking-[0.18em] ${showSolid ? "text-foreground-600" : "text-background-200"}`}>
              wycieczki
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/" className={`${linkBase} ${linkStyle}`}>{t("nav_home")}</Link>
          <button onClick={scrollToSection("#excursions")} className={`${linkBase} ${linkStyle}`}>{t("nav_excursions")}</button>
          <button onClick={scrollToSection("#contact")} className={`${linkBase} ${linkStyle}`}>{t("nav_contact")}</button>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://wa.me/48792842613"
            target="_blank"
            rel="noopener noreferrer"
            className={`hidden lg:flex items-center gap-2 text-xs transition-colors cursor-pointer ${showSolid ? "text-foreground-700 hover:text-accent-600" : "text-background-100 hover:text-accent-200"}`}
          >
            <i className="ri-whatsapp-line text-lg"></i>
            <span>WhatsApp</span>
          </a>
          <LanguageSwitcher dark={!showSolid} />
          <button
            onClick={scrollToSection("#excursions")}
            className="bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("nav_book_now")}
          </button>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <LanguageSwitcher compact dark={!showSolid} />
          <button
            onClick={() => setOpen((s) => !s)}
            className={`w-10 h-10 flex items-center justify-center rounded-md ${showSolid ? "text-foreground-950" : "text-background-50"} cursor-pointer`}
            aria-label="Menu"
          >
            <i className={`text-2xl ${open ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 flex flex-col gap-3">
            <Link to="/" className="text-foreground-900 py-2 cursor-pointer">{t("nav_home")}</Link>
            <button onClick={scrollToSection("#excursions")} className="text-foreground-900 py-2 text-left cursor-pointer">{t("nav_excursions")}</button>
            <button onClick={scrollToSection("#contact")} className="text-foreground-900 py-2 text-left cursor-pointer">{t("nav_contact")}</button>
            <a
              href="https://wa.me/48792842613"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-accent-700 py-2 cursor-pointer"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              WhatsApp
            </a>
            <button
              onClick={scrollToSection("#excursions")}
              className="bg-accent-500 text-background-50 text-center py-3 rounded-md font-medium cursor-pointer"
            >
              {t("nav_book_now")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}